import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Paper, Tabs, Tab, Badge, useTheme } from "@mui/material";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import { useAuth } from "../../../context";
import { useHighlightData } from "../../../context/useHighlightData";
import { useNewRecords } from "../../../context/NewRecordsProvider";

const tabStyle = {
  textTransform: "capitalize",
  fontWeight: "bold",
  borderRadius: "10px",
  transition: "all 0.3s ease",
};

const highlightTabStyle = {
  backgroundColor: "#FFEBEE",
  color: "#D32F2F",
  fontWeight: "bold",
  borderRadius: "10px",
};

const ReceptionNav = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const { newRecords } = useNewRecords();
  const [value, setValue] = useState("/reception");
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Type-specific collection data/highlights commented out – use Payment collection instead.
  // const { data: collect_medicine_order } = useFetchObjects(
  //   ["medicineOrdersNav"],
  //   `medicine-orders/`,
  //   token
  // );
  // const { data: collect_prescription_fee } = useFetchObjects(
  //   ["collectPrescriptionNav"],
  //   `prescriptions/`,
  //   token
  // );
  // const { data: collect_xray_fee } = useFetchObjects(
  //   ["collectXrayFeesNav"],
  //   `patient-xray/`,
  //   token
  // );
  // const { data: collect_echo_fee } = useFetchObjects(
  //   ["collectEchoFeesNav"],
  //   `patient-echo/`,
  //   token
  // );
  // const { data: collect_ultrasound_fee } = useFetchObjects(
  //   ["collectUltrasoundFeesNav"],
  //   `ultrasounds/`,
  //   token
  // );
  // const { data: collect_laboratory_fee } = useFetchObjects(
  //   ["collectLaboratoryFeesNav"],
  //   `laboratory-test-orders/`,
  //   token
  // );
  const { data: collect_emergency_fee } = useFetchObjects(
    ["collectEmergencyFeesNav"],
    `emergency-visits/`,
    token,
  );
  // useHighlightData(collect_medicine_order, "medicineOrderFees", (data) =>
  //   data.some((order) => order.payment_status === 1)
  // );
  // useHighlightData(collect_prescription_fee, "prescriptionFees", (data) =>
  //   data.some((prescription) => prescription.payment_status === 1)
  // );
  // useHighlightData(collect_xray_fee, "xrayFees", (data) =>
  //   data.some((xray) => xray.is_paid === false)
  // );
  // useHighlightData(collect_echo_fee, "echoFees", (data) =>
  //   data.some((echo) => echo.is_paid === false)
  // );
  // useHighlightData(collect_ultrasound_fee, "ultrasoundFees", (data) =>
  //   data.some((ultrasound) => ultrasound.is_paid === false)
  // );
  // useHighlightData(collect_laboratory_fee, "laboratoryFees", (data) =>
  //   data.some((labTest) => labTest.is_paid === false)
  // );
  const emergencyCondition = useCallback(
    (data) => data.some((visit) => visit.payment_status === 1),
    []
  );

  useHighlightData(collect_emergency_fee, "emergencyFees", emergencyCondition);

  useEffect(() => {
    const path = location.pathname;
    setValue(path);
  }, [location.pathname]);

  const tabs = [
    { path: "/reception", label: t("reception.dashboard") },
    { path: "/reception/new-patient", label: t("reception.newPatient") },
    { path: "/reception/patient-list", label: t("reception.patientList") },
    {
      path: "/reception/patient-referral",
      label: t("reception.patientReferral") || "Patient Referral",
    },
    { path: "/reception/appointments", label: t("reception.OPD") },
    { path: "/reception/ticketing", label: "Ticketing" },
    {
      path: "/reception/payment-collection",
      label: t("reception.paymentCollection", "Payment collection"),
    },
    // Type-specific collection tabs commented out – use Payment collection (by ticket) instead.
    // {
    //   path: "/reception/collect-prescription-fees",
    //   label: t("reception.prescriptionFees"),
    //   highlight: newRecords.prescriptionFees,
    // },
    {
      path: "/reception/collect-medicine-order-fees",
      label: t("reception.medicineFees"),
      highlight: newRecords.medicineOrderFees,
    },
    {
      path: "/reception/collect-dental-fees",
      label: t("reception.collectDentalFees"),
    },
    // {
    //   path: "/reception/collect-beauty-fees",
    //   label: t("reception.beautyFees"),
    // },
    // {
    //   path: "/reception/collect-ht-patient-fees",
    //   label: t("reception.htPatientFees"),
    // },
    // {
    //   path: "/reception/collect-ultrasound-fees",
    //   label: t("reception.ultrasoundFees"),
    //   highlight: newRecords.ultrasoundFees,
    // },
    // {
    //   path: "/reception/collect-xray-fees",
    //   label: t("reception.xrayFees"),
    //   highlight: newRecords.xrayFees,
    // },
    // {
    //   path: "/reception/collect-laboratory-fees",
    //   label: t("reception.laboratoryFees"),
    //   highlight: newRecords.laboratoryFees,
    // },
    // {
    //   path: "/reception/collect-echo-fees",
    //   label: t("reception.echoFees"),
    //   highlight: newRecords.echoFees,
    // },
    {
      path: "/reception/collect-emergency-fees",
      label: t("reception.emergencyFees"),
      highlight: newRecords.emergencyFees,
    },
    {
      path: "/reception/collect-ipd-fees",
      label: t("reception.ipdFees") || "IPD Fees",
    },
    {
      path: "/reception/collect-icu-fees",
      label: t("reception.icuFees") || "ICU Fees",
    },
    // {
    //   path: "/reception/expenses",
    //   label: t("finance.expenses"),
    // },
  ];

  return (
    <Grid container>
      <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
        <Paper sx={{ padding: "10px 0px", boxShadow: "0px 0px 0px" }}>
          <Tabs
            value={value}
            onChange={(e, newValue) => {
              setValue(newValue);
              navigate(newValue);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ ml: "5px", "& .MuiTabs-indicator": { display: "none" } }}
          >
            {tabs.map(({ path, label, highlight }) => (
              <Tab
                key={path}
                sx={{ mr: 2 }}
                style={
                  highlight
                    ? highlightTabStyle
                    : {
                        ...tabStyle,
                        backgroundColor:
                          value === path
                            ? theme?.palette.primary.main
                            : "white",
                        color: value === path ? "white" : "black",
                      }
                }
                label={
                  <Badge color="error" invisible={!highlight}>
                    {label}
                  </Badge>
                }
                value={path}
                component={NavLink}
                to={path}
              />
            ))}
          </Tabs>
        </Paper>
      </Box>
      <Outlet />
    </Grid>
  );
};

export default ReceptionNav;
